import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  ArrowCounterClockwise,
  ChatCircleDots,
  Clock,
  PaperPlaneTilt,
  Spinner,
  VideoCamera,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

function ago(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function LeaderThreadRow({ thread: t, view, onArchive, onUnarchive }) {
  const isArchived = view === "archived";
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div className="relative group">
      <Link
        to={`/coach/thread/${t.id}`}
        data-testid={`my-thread-${t.id}`}
        className="block bg-white border border-[#E8E4DB] rounded-2xl p-5 hover:border-[#1E3A2B]/50 hover:shadow-sm transition-all"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {t.unread && !isArchived && (
              <span
                className="w-2.5 h-2.5 rounded-full bg-[#C77D5E] flex-shrink-0 mt-1.5"
                data-testid="leader-unread-dot"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[#121815]">
                  Conversation {t.id.slice(0, 8).toUpperCase()}
                </span>
                {t.scheduled_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1E3A2B]/10 text-[#1E3A2B] text-[11px] font-medium">
                    {t.scheduled_medium === "audio" ? (
                      <Clock size={11} weight="duotone" />
                    ) : (
                      <VideoCamera size={11} weight="duotone" />
                    )}
                    {fmtDateTime(t.scheduled_at)}
                  </span>
                )}
                {isArchived && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8A948E]/15 text-[#4A524C] text-[11px] font-medium">
                    Archived
                  </span>
                )}
              </div>
              <p className="text-sm text-[#4A524C] mt-1 line-clamp-2 pr-16">
                {t.preview || <span className="italic text-[#8A948E]">No preview</span>}
              </p>
            </div>
          </div>
          <div className="text-xs text-[#8A948E] flex-shrink-0 flex items-center gap-1">
            <PaperPlaneTilt size={12} weight="duotone" />
            {ago(t.last_coach_message_at || t.last_user_message_at || t.created_at)}
          </div>
        </div>
      </Link>
      <div className="absolute right-4 bottom-4 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        {isArchived ? (
          <button
            onClick={(e) => {
              stop(e);
              onUnarchive(t);
            }}
            data-testid={`leader-thread-unarchive-${t.id}`}
            aria-label="Unarchive conversation"
            title="Move back to active"
            className="p-2 rounded-lg bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B]"
          >
            <ArrowCounterClockwise size={15} weight="duotone" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              stop(e);
              onArchive(t);
            }}
            data-testid={`leader-thread-archive-${t.id}`}
            aria-label="Archive conversation"
            title="Hide from my list"
            className="p-2 rounded-lg bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B]"
          >
            <Archive size={15} weight="duotone" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function LeaderConversations() {
  const { leader } = useAuth();
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({ active: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active");

  const load = useCallback(async (currentView) => {
    try {
      const { data } = await api.get(`/leader/threads?view=${currentView}`);
      setThreads(data.threads || []);
      if (data.counts) setCounts(data.counts);
    } catch (err) {
      toast.error(apiError(err, "Could not load conversations"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!leader) return;
    load(view);
  }, [leader, view, load]);

  const handleArchive = async (t) => {
    try {
      await api.post(`/leader/threads/${t.id}/archive`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      setCounts((c) => ({ active: Math.max(0, c.active - 1), archived: c.archived + 1 }));
      toast.success("Hidden from your list.", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/leader/threads/${t.id}/unarchive`);
              toast.success("Restored.");
              load(view);
            } catch (err) {
              toast.error(apiError(err, "Could not undo"));
            }
          },
        },
      });
    } catch (err) {
      toast.error(apiError(err, "Could not archive"));
    }
  };

  const handleUnarchive = async (t) => {
    try {
      await api.post(`/leader/threads/${t.id}/unarchive`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      setCounts((c) => ({ active: c.active + 1, archived: Math.max(0, c.archived - 1) }));
      toast.success("Back in your list.", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/leader/threads/${t.id}/archive`);
              toast.success("Hidden again.");
              load(view);
            } catch (err) {
              toast.error(apiError(err, "Could not undo"));
            }
          },
        },
      });
    } catch (err) {
      toast.error(apiError(err, "Could not unarchive"));
    }
  };

  const visible = useMemo(() => threads, [threads]);

  if (leader === null) {
    return (
      <div className="text-center text-[#8A948E] py-16 flex items-center justify-center gap-2">
        <Spinner size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!leader)
    return <Navigate to="/auth/login" state={{ from: "/my/conversations" }} replace />;

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Your conversations</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          {leader.name?.split(" ")[0] || "Welcome"}.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">Pick up any thread with your coach.</p>
      </div>

      <div className="mt-10 anim-in delay-2 flex items-center gap-2 flex-wrap">
        <Link
          to="/coach"
          data-testid="leader-new-conversation"
          className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-3 rounded-xl hover:bg-[#2C4C3B]"
        >
          <ChatCircleDots size={16} weight="duotone" />
          Start a new conversation
        </Link>
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap anim-in delay-3">
        <button
          onClick={() => setView("active")}
          data-testid="leader-view-active"
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            view === "active"
              ? "bg-[#1E3A2B] text-white"
              : "bg-white text-[#4A524C] border border-[#E8E4DB] hover:border-[#1E3A2B]"
          }`}
        >
          Active · {counts.active}
        </button>
        <button
          onClick={() => setView("archived")}
          data-testid="leader-view-archived"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all ${
            view === "archived"
              ? "bg-[#1E3A2B] text-white"
              : "bg-white text-[#4A524C] border border-[#E8E4DB] hover:border-[#1E3A2B]"
          }`}
        >
          <Archive size={14} weight="duotone" />
          Archived · {counts.archived}
        </button>
      </div>

      <section className="mt-6 anim-in delay-3">
        {loading ? (
          <div className="text-[#8A948E] text-sm flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Loading…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center text-[#8A948E] bg-[#F3F0EA] rounded-2xl px-6 py-12">
            <ChatCircleDots size={36} weight="duotone" className="mx-auto text-[#1E3A2B]/40 mb-3" />
            <div className="font-medium text-[#4A524C]">
              {view === "archived" ? "No archived conversations." : "No conversations yet."}
            </div>
            {view !== "archived" && (
              <div className="text-sm mt-1">Start one above and your coach will respond.</div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((t) => (
              <LeaderThreadRow
                key={t.id}
                thread={t}
                view={view}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
